/**
 * 侧边栏导航组件
 * 根据路由配置渲染导航链接，支持折叠/展开
 */
import { NavLink, useLocation } from 'react-router-dom';
import { Activity, X } from 'lucide-react';
import { appRoutes } from './routes';

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

/** 侧边栏导航组件：根据路由配置渲染导航链接，支持桌面折叠与移动端抽屉 */
export function Sidebar({ collapsed, mobileOpen, onCloseMobile }: SidebarProps) {
  const location = useLocation();

  return (
    <>
      {mobileOpen && (
        <button
          aria-label="关闭导航"
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={onCloseMobile}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[260px] shrink-0 flex-col border-r border-surface-700 bg-surface-900 transition-[width,transform] duration-200 lg:static lg:z-auto lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'lg:w-[76px]' : 'lg:w-60'}`}
      >
        <div className={`flex h-[72px] shrink-0 items-center border-b border-surface-700/80 ${collapsed ? 'lg:justify-center lg:px-0' : 'px-5'}`}>
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-accent-400/30 bg-accent-500/10 text-accent-300">
              <Activity className="h-5 w-5" strokeWidth={2.25} />
            </div>
            <div className={`min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <div className="text-sm font-bold tracking-[0.14em] text-surface-50">FLUXLAB</div>
              <div className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-surface-500">Motor control bench</div>
            </div>
          </div>
          <button
            aria-label="关闭导航"
            className="ml-auto rounded p-2 text-surface-400 hover:bg-surface-800 hover:text-surface-100 lg:hidden"
            onClick={onCloseMobile}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5">
          <div className={`mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-surface-600 ${collapsed ? 'lg:hidden' : ''}`}>
            工作台
          </div>
          <div className="space-y-1">
            {appRoutes
              .filter((r) => !r.hidden)
              .map((route) => {
                const isActive = location.pathname === route.path;
                return (
                  <NavLink
                    key={route.path}
                    to={route.path}
                    title={collapsed ? route.label : undefined}
                    onClick={onCloseMobile}
                    className={`group relative flex h-11 items-center gap-3 rounded-md px-3 text-sm transition-colors ${
                      isActive
                        ? 'bg-accent-500/10 text-accent-300'
                        : 'text-surface-400 hover:bg-surface-800 hover:text-surface-100'
                    } ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
                  >
                    {isActive && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-accent-400" />}
                    <span className="shrink-0 [&>svg]:h-[18px] [&>svg]:w-[18px]">{route.icon}</span>
                    <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{route.label}</span>
                  </NavLink>
                );
              })}
          </div>
        </nav>

        <div className={`border-t border-surface-700/80 p-4 ${collapsed ? 'lg:px-3' : ''}`}>
          <div className={`flex items-center gap-3 rounded-md bg-surface-950/60 p-3 ${collapsed ? 'lg:justify-center lg:p-2' : ''}`}>
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-400 opacity-20" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent-500" />
            </span>
            <div className={`min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <div className="text-[11px] font-medium text-surface-300">FluxLab v1.0</div>
              <div className="text-[10px] text-surface-600">STM32 MCSDK</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
